import React from 'react';
import { lightTheme, MODULE_THEMES } from '../../Theme';
import { Box, Stack } from '../../ui';
import { Module, ModuleType } from '../../Module';
import { TemplateType } from '../index';
import { HelpHeader } from '../../Help/HelpHeader';
import { HelpFAQ } from '../../Help/HelpFAQ';
import { TopicGrid } from '../../Help/TopicGrid';
import { kleinBlue } from '@spotify-internal/encore-foundation';
import { ThemeProvider } from 'styled-components';
import { useDynamicNavBarColor } from '../useDynamicNavBarColor';
import { FaqCategory, FaqArticle } from '../../Help/util';
import { itemToSearchHit, SearchHit } from '../../Search';
import { ByFaqSlug } from '../../Help/getHelpData';

export type HelpHomeTemplateType = TemplateType & {
  headline?: string;
  frequentlyAskedQuestions?: FaqArticle[];
  extraCategories?: FaqCategory[];
  faqCategories?: FaqCategory[];
  modules?: ModuleType[];
  byFaqSlug?: ByFaqSlug;
};

export const HelpHomeTemplate: React.FC<HelpHomeTemplateType> = template => {
  const {
    headline = 'Creator Marketing Support',
    frequentlyAskedQuestions,
    faqCategories,
    modules,
  } = template;

  const allModules: any = [...(modules || [])].filter(m =>
    Boolean(m?.contentType),
  );

  useDynamicNavBarColor({ backgroundColor: kleinBlue });

  const renderModule = React.useCallback((mod: ModuleType) => {
    const modProps = { ...mod } as any;
    return (
      <div>
        <Module {...modProps} />
      </div>
    );
  }, []);

  const modTheme = (mod: ModuleType) => ({
    ...lightTheme,
    ...(MODULE_THEMES[mod.themeName || ''] || {}),
  });

  const faqs = frequentlyAskedQuestions
    ?.map(itemToSearchHit)
    .filter(Boolean) as NonNullable<SearchHit>[];

  return (
    <ThemeProvider theme={lightTheme}>
      <Stack data-testid={`help-home-${template.urlSlug || 'unknown'}`}>
        <HelpHeader headline={headline} backgroundColor={kleinBlue} />
        {frequentlyAskedQuestions && <HelpFAQ items={faqs} />}
        {faqCategories && <TopicGrid items={faqCategories} />}
        {allModules.map((mod: ModuleType, idx: number) => (
          <ThemeProvider key={`${mod.id}-${idx}`} theme={modTheme(mod)}>
            <Box>{renderModule(mod)}</Box>
          </ThemeProvider>
        ))}
      </Stack>
    </ThemeProvider>
  );
};
